import { ReactElement, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';
import { CabIcon } from '@CabComponents/CabIcon';
import { IoDocumentTextOutline } from 'react-icons/io5';
import colors from '../../colors';
import CabRTEditor from '@CabComponents/CabRTEditor';
import { EventBriefExternal, PublicOrganization } from '../../store/cabinetApi/generated/crm';

// eslint-disable-next-line max-len
const EMPTY_STATE = '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';

interface FormData {
  notes: string;
}

export interface PublicBriefProps {
  briefData: EventBriefExternal | null | undefined;
  loading: boolean;
  error: string | null;
  onNotesUpdate: (content: string) => void;
  organization: PublicOrganization | null | undefined;
}

export const PublicBrief = ({
  briefData,
  loading,
  error,
  onNotesUpdate,
  organization,
}: PublicBriefProps): ReactElement => {
  // Create form values based on briefData
  const formValues = useMemo(() => ({
    notes: briefData?.notes ? JSON.stringify(briefData.notes) : EMPTY_STATE,
  }), [briefData?.notes]);

  const { control, watch } = useForm<FormData>({ 
    values: formValues 
  });

  const notesContent = watch('notes');

  // Debounced update function
  const debouncedUpdate = useDebouncedCallback((content: string) => {
    onNotesUpdate(content);
  }, 5000); // 5 seconds debounce

  // Watch for notes changes and trigger debounced update
  useEffect(() => {
    if (notesContent && notesContent !== EMPTY_STATE) {
      debouncedUpdate(notesContent);
    }
  }, [notesContent, debouncedUpdate]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px"
        }}>
        <Typography>Loading brief...</Typography>
      </Box>
    );
  }

  if (error || !briefData) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "400px",
          gap: 2
        }}>
        <CabIcon Icon={IoDocumentTextOutline} sx={{ fontSize: 48, color: colors.black400 }} />
        <Typography variant="h5" color={colors.black600}>
          Meeting Brief Not Found
        </Typography>
        <Typography color={colors.black500}>
          {error || 'The meeting brief you are looking for does not exist or has been removed.'}
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      p: 0,
      height: 'calc(100vh - 64px)', // Account for Container padding
      display: 'flex',
      flexDirection: 'column'
    }}>
      {organization?.logo && (
        <Box sx={{
          flex: 0, display: 'flex', flexDirection: 'column', mb: 3, justifyContent: 'center', alignItems: 'center',
        }}>
          <img src={organization?.logo} alt="Logo" height={60} />
        </Box>
      )}
      {/* Brief Section - Takes up remaining space */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, mb: 3 }}>
        <Typography
          variant="h6"
          color={colors.navyDark}
          sx={{
            fontWeight: 600,
            mb: 2
          }}>
          Meeting Brief
        </Typography>
        <Box sx={{ 
          flex: 1,
          border: `1px solid ${colors.black200}`, 
          borderRadius: 1,
          backgroundColor: colors.white900,
          overflow: 'auto',
          // '& .editor-input': {
          //   maxHeight: '300px !important',
          //   overflow: 'auto !important',
          //   height: '100% !important',
          //   // minHeight: '150px !important',
          //   // height: '300px !important'
          // }
        }}>
          <CabRTEditor
            placeholder=""
            onChange={() => {}} // Read-only, no onChange needed
            editorState={briefData.content ? JSON.stringify(briefData.content) : EMPTY_STATE}
            readOnly={true}
          />
        </Box>
      </Box>
      {/* Meeting Notes - Fixed height at bottom */}
      <Box sx={{ flexShrink: 0 }}>
        <Typography
          variant="h6"
          color={colors.navyDark}
          sx={{
            fontWeight: 600,
            mb: 2
          }}>
          Notes
        </Typography>
        <Box sx={{ 
          border: `1px solid ${colors.black200}`, 
          borderRadius: 1,
          backgroundColor: colors.white900,
          height: 300,
          overflow: 'auto',
          '& .editor-input': {
            maxHeight: '300px !important',
            overflow: 'auto !important',
            height: '300px !important'
          }
        }}>
          <Controller
            name="notes"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <CabRTEditor
                key={`notes-${briefData?.id || 'loading'}-${field.value !== EMPTY_STATE ? 'has-content' : 'empty'}`}
                placeholder="Add your meeting notes here..."
                {...field}
                editorState={field.value}
                autoFocus={false}
              />
            )}
          />
        </Box>
      </Box>
    </Box>
  );
}; 